import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import "./BlogDetailsPage.css";

const token = localStorage.getItem("token");
const API_BASE = process.env.REACT_APP_API_URL || "https://api.goharesadaf.ir";

export default function BlogDetails() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [newRating, setNewRating] = useState(0);
  const [loadingComments, setLoadingComments] = useState(true);
  const [commentsError, setCommentsError] = useState(null);
  const [expandedComments, setExpandedComments] = useState(new Set());
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loadingRelatedProducts, setLoadingRelatedProducts] = useState(false);
  const [relatedProductsError, setRelatedProductsError] = useState(null);
  const [otherBlogs, setOtherBlogs] = useState([]);
  const [loadingOthers, setLoadingOthers] = useState(true);

  // fetch main blog
  useEffect(() => {
  async function fetchBlog() {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/blogs/${slug}`);
      if (!res.ok) throw new Error('Blog یافت نشد');
      const data = await res.json();
      // data is now expected to include related_products & related_blogs
      setBlog({
        id: data.id,
        title: data.title,
        content: data.content,
        image: data.image,
        created_at: data.created_at,
        category: data.category || null,
      });
      // set related items if present:
      setOtherBlogs(data.related_blogs ?? []);
      setRelatedProducts(data.related_products ?? []);
    } catch (err) {
      setError(err.message || 'خطا در بارگذاری مقاله');
    } finally {
      setLoading(false);
    }
  }
  fetchBlog();
}, [slug]);


  useEffect(() => { async function fetchOtherBlogs() { try {
        const res = await fetch(`${API_BASE}/api/blogs`);
        const data = await res.json();
        // exclude current blog
        const filtered = data.filter((b) => b.slug !== slug);
        setOtherBlogs(filtered.slice(0, 6)); // only 6
      } catch (err) {
        console.error("fetchOtherBlogs error:", err);
      } finally {
        setLoadingOthers(false);
      }
    }
    fetchOtherBlogs();
  }, [slug]);

  // fetch comments (depends on blog)
  useEffect(() => {
    if (!blog) return;
    const fetchComments = async () => {
      setLoadingComments(true);
      setCommentsError(null);
      try {
        const res = await fetch(
          `${API_BASE}/api/comments?commentable_type=Blog&commentable_id=${blog.id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const data = await res.json();
        setComments(data);
      } catch (err) {
        setCommentsError(err.message || "خطا در دریافت نظرات");
      } finally {
        setLoadingComments(false);
      }
    };
    fetchComments();
  }, [blog]);

  const toggleExpand = (id) => {
    setExpandedComments((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return s;
    });
  };

  const isLongComment = (text) =>
    text && (text.length > 260 || (text.match(/\n/g) || []).length >= 4);

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      const res = await fetch(`${API_BASE}/api/comments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          commentable_type: "Blog",
          commentable_id: blog.id,
          content: newComment,
        }),
      });

      if (!res.ok) throw new Error("خطا در ارسال نظر");

      const savedComment = await res.json();
      setComments([savedComment, ...comments]);
      setNewComment("");
      setNewRating(0);
    } catch (err) {
      alert(err.message);
    }
  };

  const handleAddToCart = async (productId) => {
    try {
      const res = await fetch(`${API_BASE}/api/cart/add`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ product_id: productId, quantity: 1 }),
      });

      if (!res.ok) throw new Error("خطا در افزودن محصول به سبد خرید");

      // Optional: Show toast notification
      alert("محصول به سبد خرید اضافه شد ✅");

    } catch (err) {
      console.error(err);
      alert(err.message);
    }
  };


  const getInitials = (name = "") =>
    name
      .split(" ")
      .map((s) => s.trim()[0] || "")
      .slice(0, 2)
      .join("")
      .toUpperCase();

  const fmtDate = (iso) =>
    new Date(iso).toLocaleString("fa-IR", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  if (loading) return <div className="blog-loading">در حال بارگذاری مقاله...</div>;
  if (error) return <div className="blog-error">{error}</div>;

  return (
    <div className="blog-details-layout">
      <main className="blog-details-main">
        <button className="back-button" onClick={() => navigate(-1)}>
          ← بازگشت
        </button>
        <h1 className="blog-title">{blog.title}</h1>

        {blog.image && (
          <img src={blog.image} alt={blog.title} className="blog-cover" />
        )}

        <p className="blog-date">
          {new Date(blog.created_at).toLocaleDateString("fa-IR")}
        </p>

        <div
          className="blog-content"
          dangerouslySetInnerHTML={{ __html: blog.content }}
        />

        {/* comments */}
        <div className="comments-section" dir="rtl" aria-live="polite">
          <div className="comments-header">
            <h3>نظرات</h3>
          </div>

          {loadingComments ? (
            <div className="comments-loading">در حال بارگذاری نظرات...</div>
          ) : commentsError ? (
            <div className="comments-error">{commentsError}</div>
          ) : (
            <>
              {/* add comment */}
              <div className="add-comment-card">
                <div className="avatar-col" aria-hidden>
                  <div className="avatar">شما</div>
                </div>

                <div className="input-col">
                  <textarea
                    placeholder="نظر خود را دربارهٔ این مقاله بنویسید..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    className="comment-textarea"
                    maxLength={1000}
                    rows={4}
                  />

                  <div className="bottom-row">
                    <div className="actions">
                      <button
                        className="btn-primary"
                        onClick={handleAddComment}
                        disabled={!newComment.trim()}
                      >
                        ارسال
                      </button>
                      <button
                        className="btn-ghost"
                        onClick={() => {
                          setNewComment("");
                          setNewRating(0);
                        }}
                        type="button"
                      >
                        پاک کردن
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* comments list */}
              <div className="comments-list">
                {comments.length === 0 ? (
                  <div className="no-comments">
                    هنوز نظری ثبت نشده است — اولین نفر باشید!
                  </div>
                ) : (
                  comments.map((c) => {
                    const expanded = expandedComments.has(c.id);
                    const long = isLongComment(c.content);

                    return (
                      <article key={c.id} className="comment-card">
                        <div className="comment-left" aria-hidden>
                          <div className="comment-avatar">
                            <div className="avatar initials">
                              {getInitials(c.user?.name || "مهمان")}
                            </div>
                          </div>
                        </div>

                        <div className="comment-body">
                          <div className="comment-meta">
                            <h4 className="comment-author">
                              {c.user?.name || "کاربر مهمان"}
                            </h4>
                            <div className="comment-meta-right">
                              <time
                                className="comment-date"
                                dateTime={c.created_at}
                              >
                                {fmtDate(c.created_at)}
                              </time>
                            </div>
                          </div>

                          <p
                            className={`comment-content ${
                              expanded ? "expanded" : ""
                            }`}
                          >
                            {c.content}
                          </p>

                          {long && (
                            <button
                              className="comment-more"
                              onClick={() => toggleExpand(c.id)}
                            >
                              {expanded ? "نمایش کمتر" : "نمایش بیشتر"}
                            </button>
                          )}
                        </div>
                      </article>
                    );
                  })
                )}
              </div>
            </>
          )}
        </div>
      </main>

      <aside className="blog-sidebar">

        <div className="sidebar-block">
          <h4>محصولات مرتبط</h4>
          {loadingRelatedProducts ? (
            <p>در حال بارگذاری...</p>
          ) : relatedProductsError ? (
            <p className="small-error">{relatedProductsError}</p>
          ) : relatedProducts.length === 0 ? (
            <p className="small-muted">محصول مرتبطی یافت نشد.</p>
          ) : (
            <ul className="related-products-list">
              {relatedProducts.map((p) => (
                <li key={p.id} className="related-product-item">
                  <div
                    className="related-prod-info"
                    onClick={() => navigate(`/product/${p.id}`)}
                  >
                    <img
                      src={p.main_image || ""}
                      alt={p.title}
                      className="related-prod-thumb"
                    />
                    <div className="related-prod-meta">
                      <div className="related-prod-title">{p.title}</div>
                      {p.price ? (
                        <div className="related-prod-price">
                          {Number(p.price).toLocaleString("fa-IR")} تومان
                        </div>
                      ) : null}
                    </div>
                  </div>

                  {/* Add to Cart button */}
                  <button
                    className="add-to-cart-btn"
                    onClick={(e) => {
                      e.stopPropagation(); // prevent navigation
                      handleAddToCart(p.id);
                    }}
                  >
                    افزودن به سبد
                  </button>
                </li>
              ))}
            </ul>

          )}
        </div>

        <div className="sidebar-block">
          <h3>مقالات دیگر</h3>
          {loadingOthers ? (
            <p>در حال بارگذاری...</p>
          ) : (
            <ul className="other-blogs-list">
              {otherBlogs.map((b) => (
                <li key={b.id} className="other-blog-item">
                  <Link to={`/blogs/${b.slug}`}>
                    {b.image && (
                      <img
                        src={b.image}
                        alt={b.title}
                        className="other-blog-thumb"
                      />
                    )}
                    <span>{b.title}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>


      </aside>




    </div>
  );
}
